import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API } from '../App';
import { FORMATTED_STATS } from '../constants/siteStats';

const Clients = () => {
  // Default clients with logo images - Ordered as requested
  const defaultClients = [
    // Row 1
    { name: 'KFC', logo: '/images/clients/kfc-logo-2026.png' },
    { name: "McDonald's", logo: '/images/clients/mcdonalds.png' },
    { name: "Domino's", logo: '/images/clients/dominos.png' },
    // Row 2
    { name: 'Adventure World', logo: '/images/clients/adventure-world.png' },
    { name: 'Costco', logo: '/images/clients/costco.webp' },
    { name: 'BaptistCare', logo: '/images/clients/baptist-care.png' },
    // Row 3
    { name: 'SilverChef', logo: '/images/clients/silverchef.png' },
    { name: 'Two Slices', logo: '/images/clients/two-slices.webp' },
    { name: 'The Wray Hotel', logo: '/images/clients/wray-hotel.png' },
    // Row 4
    { name: 'The Last Local', logo: '/images/clients/last-local.png' },
    { name: 'Latitude 32', logo: '/images/clients/latitude32.png' },
    { name: 'The Cray', logo: '/images/clients/the-cray.webp' },
    // Rest in any order
    { name: '7th Avenue Bar', logo: '/images/clients/7th-avenue-bar.png' },
    { name: 'City of Canning', logo: '/images/clients/city-of-canning.webp' },
    { name: 'Crust', logo: '/images/clients/crust.png' },
    { name: 'Daisies Cottesloe', logo: '/images/clients/daisies-cottesloe.webp' },
    { name: 'Dome', logo: '/images/clients/dome.jpg' },
    { name: 'Hilton Pizza', logo: '/images/clients/hilton-pizza.jpg' },
    { name: 'Kalgoorlie Hotel', logo: '/images/clients/kalgoorlie-hotel.jpeg' },
    { name: 'The Kwinana Local', logo: '/images/clients/kwinana-local.jpg' },
    { name: "Nando's", logo: '/images/clients/nandos.png' },
    { name: 'Pizza Hut', logo: '/images/clients/pizza-hut.jpg' },
    { name: "Romano's", logo: '/images/clients/romanos.png' },
    { name: 'Side Piece', logo: '/images/clients/side-piece.webp' },
  ];

  const [clients, setClients] = useState(defaultClients);
  const [settings, setSettings] = useState({
    stats_years: `${new Date().getFullYear() - 2006}+`,
    stats_clients: FORMATTED_STATS.activeClients,
    stats_jobs: FORMATTED_STATS.jobsCompletedThisYear
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes] = await Promise.all([
          axios.get(`${API}/clients`)
        ]);
        if (clientsRes.data && clientsRes.data.length > 0) {
          setClients(clientsRes.data);
        }
        // Stats are always taken from centralised constants, not the API
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/clients" />
        <title>Our Clients | Extreme Clean Australia</title>
      </Helmet>
      <div className="pt-24 lg:pt-44" data-testid="clients-page">
      {/* Hero */}
      <section className="bg-[#273268] py-24" data-testid="clients-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-4">Our Clients</p>
            <h1 className="font-heading font-bold text-5xl md:text-6xl leading-snug text-white mb-6">
              Trusted by Leading Brands
            </h1>
            <p className="text-gray-400 text-lg">
              We're proud to serve some of Australia's most recognised brands and local Western Australian businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#3B82F6] py-12" data-testid="clients-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div data-testid="stat-clients">
              <p className="font-mono text-4xl font-bold text-white">{settings.stats_clients}</p>
              <p className="text-blue-100 text-sm uppercase tracking-wider">Active Clients</p>
            </div>
            <div data-testid="stat-jobs">
              <p className="font-mono text-4xl font-bold text-white">{settings.stats_jobs}</p>
              <p className="text-blue-100 text-sm uppercase tracking-wider">Jobs Completed This Year</p>
            </div>
            <div data-testid="stat-years">
              <p className="font-mono text-4xl font-bold text-white">{settings.stats_years}</p>
              <p className="text-blue-100 text-sm uppercase tracking-wider">Years Serving WA</p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section className="py-24 bg-white" data-testid="client-logos">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Featured Clients</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-[#0A0A0A]">Brands That Trust Us</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {clients.map((client, index) => (
              <div 
                key={client.id || index}
                className="bg-gray-50 p-6 flex items-center justify-center h-40 rounded-lg hover:shadow-lg transition-all duration-300 group border border-gray-100 hover:border-[#3B82F6]"
                data-testid={`client-logo-${index}`}
              >
                <img 
                  src={client.logo} 
                  alt={`${client.name} logo`}
                  className="max-h-28 max-w-full object-contain transition-all duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <p className="text-[#0A0A0A] font-heading font-bold text-lg hidden">{client.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-[#273268]" data-testid="industries">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Industries</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white">Sectors We Serve</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              'Fast Food & QSR',
              'Restaurants & Cafes',
              'Hotels & Hospitality',
              'Retail & Commercial',
              'Theme Parks',
              'Aged Care',
              'Local Government',
              'Bars & Pubs'
            ].map((industry, index) => (
              <div 
                key={index}
                className="bg-white/5 border border-white/10 p-6 text-center hover:bg-[#3B82F6] hover:border-[#3B82F6] transition-all duration-300"
              >
                <p className="font-heading font-bold text-white">{industry}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#3B82F6]" data-testid="clients-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
            Join Our Growing Client Base
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Experience the Extreme Clean difference. Contact us today for a free quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm">
              Get a Quote
            </a>
            <a href="tel:0861875523" className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm">
              Call 08 6187 5523
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Clients;
